import { memo, useCallback, useEffect, useState } from 'react'

import Close from '@carbon/icons-react/lib/Close'
import { AnimatePresence, m, LazyMotion } from 'motion/react'
import useWindowSize from 'lib/hooks/useWindowSize'
import { defaultProps } from 'lib/layouts/DashboardLayout'
import Box from 'lib/ui/Box'
import Button from 'lib/ui/Button'
import theme from 'utils/theme'
import { spacing } from 'utils/theme/spacing'

import * as css from './SideDrawer.styles'
import { ISideDrawer } from './SideDrawer.types'

const {
  treatments: { stackTop }
} = theme

const loadFeatures = () => import('utils/animations').then(res => res.default)

const SideDrawer = ({
  brandColor,
  breakpoint = defaultProps.breakpoint,
  background = 'light',
  children,
  closeButton,
  hideOverlay,
  expandFull,
  expandWidth = '50%',
  open,
  onChange,
  padding = 2,
  styles,
  close,
  testid = 'side-drawer'
}: ISideDrawer) => {
  const [isOpen, setOpen] = useState(open)
  const { width } = useWindowSize()
  const isMobile =
    width && width < Number(theme.breakpoints[breakpoint].split('px')[0])

  const sidebar = {
    open: {
      ...stackTop,
      width: isMobile || expandFull ? '100%' : expandWidth,
      padding: spacing(padding),
      transition: {
        type: 'tween' as const,
        duration: 0.5,
        delayChildren: 0.5
      }
    },
    closed: {
      width: 0,
      padding: 0,
      zIndex: 0,
      transition: {
        type: 'tween' as const
      }
    }
  }

  const child = {
    closed: { opacity: 0 },
    open: { opacity: 1 }
  }

  useEffect(() => {
    handleToggle(open)
    return () => setOpen(false)
  }, [open])

  const handleToggle = useCallback(
    (toggle: boolean) => {
      if (onChange) onChange(toggle)
      setOpen(toggle)
    },
    [isOpen]
  )

  return (
    <LazyMotion features={loadFeatures}>
      <AnimatePresence initial={false}>
        <m.div initial={false} animate={isOpen ? 'open' : 'closed'}>
          <m.div
            css={css.root({ background, styles })}
            variants={sidebar}
            data-testid={testid}
          >
            <m.div variants={child}>
              <m.div css={css.btnWrap}>
                <m.button
                  onClick={() => handleToggle(!isOpen)}
                  css={css.btn({ close, styles })}
                >
                  <Close size={24} />
                </m.button>
              </m.div>
            </m.div>
            <m.div
              variants={child}
              css={css.content({ styles })}
              data-testid='side-drawer-content'
            >
              {children}
              {closeButton && isOpen && (
                <Box
                  padding={0}
                  margin={[1.5, 0, 0.5]}
                  width={23}
                  styles={css.close}
                >
                  <Button
                    brandColor={brandColor}
                    variant='primary'
                    onClick={() => handleToggle(!isOpen)}
                    buttonSize='lg'
                    display='block'
                  >
                    Close
                  </Button>
                </Box>
              )}
            </m.div>
          </m.div>
          {isOpen && !hideOverlay && <div css={css.overlay(isOpen)} />}
        </m.div>
      </AnimatePresence>
    </LazyMotion>
  )
}

export default memo(SideDrawer)
